import {
  boolean,
  freedraw,
  general,
  grid,
  image,
  retouching,
  useEditorStore,
} from '@modules/editor';
import {
  type GenericElement,
  deleteMultipleElements,
  updateElement,
  updateMultipleElements,
} from '@modules/editor/src/lib/element';
import { history } from '@modules/editor/src/lib/history';
import {
  renderInteractiveScene,
  renderStaticScene,
} from '@modules/editor/src/lib/scene';
import {
  clearBooleanCaches,
  type BooleanElement,
} from '@modules/editor/src/plugins/boolean';
import { type GridElement } from '@modules/editor/src/plugins/grid';
import {
  updateRuntimeStore,
  useEditorRuntimeStore,
} from '@modules/editor/src/stores/useEditorRuntimeStore';
import { createSelectorHooks } from 'auto-zustand-selectors-hook';
import { FastAverageColor } from 'fast-average-color';
import { type RefObject } from 'react';
import { create } from 'zustand';

import { t } from '$/components/core/Localization/18n';
import { trackEvent } from '$/logger';
import {
  getNextLayerForComponent,
  reassignAllComponentLayers,
} from '$/pages/CustomEditorPage/utils/layerAssignment';
import {
  type EditorView,
  migrateProjectData,
} from '$/pages/CustomEditorPage/utils/migrations';
import { plugins } from '$/pages/CustomEditorPage/utils/plugins';
import { repairProjectData } from '$/pages/CustomEditorPage/utils/repair';
import {
  deriveActiveComponentId,
  deriveSelectedComponentIds,
  isUserGeneratedElement,
} from '$/pages/CustomEditorPage/utils/selection';
import {
  applyMaterialToView,
  type ComponentMaterial,
  type ComponentTexture,
} from '$/pages/CustomEditorPage/utils/views';
import { type Material } from '$/services/usecases/materials/mapper/materials';
import { type EditorProject } from '$/services/usecases/projects/mapper/editorProject';
import { useCollectionStore } from '$/stores/useCollectionStore';
import { trackMaterialRendering } from '$/utils/piwikUtils';
import { uid } from '$/utils/uid';

const minimalDimensionSize = 500;

export type EditorComponent = {
  id: string;
  elements: string[];
  color: string;
  materialId?: string;
  shouldDisplayTexture: boolean;
  name: string;
  groupId?: string;
};

export type ViewGroupMappings = Record<string, Record<number, string>>;

type MaterialAssignment = {
  viewIndex: number;
  component: EditorComponent;
  material: ComponentMaterial;
  texture: ComponentTexture;
};

export type EditorProjectMeta = Omit<EditorProject, 'data'>;

type State = {
  components: EditorComponent[];
  views: EditorView[];
  activeViewIndex: number;
  isSwitchingViews: boolean;
  projectName: string;
  referenceMeasure: number;
  openedAccordionId: string | null;
  autosaveTimerRef: RefObject<NodeJS.Timeout | null>;
  project: EditorProjectMeta | null;
  fullscreen: boolean;
};

type Actions = {
  addNewComponent: () => void;
  deleteComponent: (id: string) => void;
  deleteComponents: (ids: string[]) => void;
  mergeComponents: (componentIds?: string[]) => void;
  associateMaterialWithComponent: (material: Material) => Promise<void>;
  replaceViewGroupMappings: (mappings: ViewGroupMappings) => Promise<void>;
  associateElementWithComponent: (
    elementId: string,
    componentId?: string,
  ) => EditorComponent | null;
  disassociateElementWithComponent: (elementId: string) => void;
  moveComponent: (componentId: string, newIndex: number) => void;
  moveElement: (elementId: string, newIndex: number) => void;
  addView: (image: {
    src: string;
    htmlImage: HTMLImageElement;
  }) => Promise<void>;
  switchView: (index: number) => Promise<void>;
  persistActiveView: () => Promise<EditorView[]>;
  loadViews: (
    views: EditorView[],
    options?: { clearHistory?: boolean; activeViewIndex?: number },
  ) => Promise<void>;
  loadProject: (
    project: EditorProject | undefined | null,
    options?: { activeViewIndex?: number },
  ) => void;
  resetStore: () => void;
  toggleFullscreen: () => void;
};

let cachedActiveComponentId = '';

/**
 * Returns the active component, falling back to the last known value
 * when the current selection doesn't map to a component
 */
export const getActiveComponentId = (): string => {
  const { components } = store.getState();
  const { selectedElementIds } = useEditorStore.getState();
  const derived = deriveActiveComponentId(components, selectedElementIds);

  if (derived) {
    cachedActiveComponentId = derived;
    return derived;
  }

  return cachedActiveComponentId;
};

const initialState: State = {
  components: [],
  views: [],
  activeViewIndex: 0,
  isSwitchingViews: false,
  projectName: '',
  referenceMeasure: 0,
  openedAccordionId: null,
  autosaveTimerRef: { current: null },
  project: null,
  fullscreen: false,
};
const store = create<State & Actions>()((set, get) => {
  const createNewComponent = (letterNumber?: number): EditorComponent => {
    const { components } = get();
    let letter = letterNumber ?? components.length + 1;
    let name = t('editor.component_name', {
      letter,
      defaultValue: 'Bauteil {{letter}}',
    });
    let maxIterations = components.length + 200;
    const existingNames = new Set(components.map((c) => c.name));

    while (existingNames.has(name) && maxIterations-- > 0) {
      letter++;
      name = t('editor.component_name', {
        letter,
        defaultValue: 'Bauteil {{letter}}',
      });
      if (!name.includes(letter.toString())) break;
    }

    if (maxIterations <= 0) {
      console.warn('Max iterations reached while creating new component');
    }

    return {
      id: uid(),
      elements: [],
      color: '#3c87f7',
      shouldDisplayTexture: false,
      name,
    };
  };

  const applyViewToEditor = async (
    view: EditorView,
    { clearHistory }: { clearHistory?: boolean } = {},
  ) => {
    set({ components: view.components });

    useEditorStore.setState({
      polygonPointsVisible: false,
      selectedSubElementIds: [],
    });

    await general.actions.importEditor(
      plugins,
      { elements: view.elements },
      { clearHistory },
    );

    general.actions.activate('selection');

    if (get().components.length === 0) {
      get().addNewComponent();
      history.clear();
      general.actions.captureHistorySnapshot();
    }

    // boolean shapes are not stored
    // -> need to be recalculated after all elements are loaded
    const allElements = useEditorStore.getState().elements;
    for (const element of allElements.values()) {
      if (element.type === 'boolean') {
        boolean.actions.reevaluateDimensions(element.id);
      }
    }

    const firstComponentId = get().components.at(0)?.id;

    if (firstComponentId) {
      useEditorStore.setState({
        selectedElementIds: [firstComponentId],
      });
    }

    const imageElement = Array.from(
      useEditorStore.getState().elements.values(),
    ).find((element) => element.type === 'image');

    updateRuntimeStore({ panConstraintElementId: imageElement?.id });

    if (imageElement) general.actions.zoomToFitElement(imageElement);

    renderStaticScene();
  };

  // Applies material assignments across views: inactive views are updated
  // on their serialized data, the active view through the live editor, so
  // that rendering and persistence agree.
  const applyMaterialAssignments = async (
    views: EditorView[],
    assignments: MaterialAssignment[],
  ) => {
    const { activeViewIndex } = get();
    let nextViews = views;

    for (const { viewIndex, component, material, texture } of assignments) {
      nextViews = nextViews.map((view, index) =>
        index === viewIndex
          ? applyMaterialToView(view, component, material, texture)
          : view,
      );
    }

    set({
      views: nextViews,
      components: nextViews[activeViewIndex].components,
    });

    const elements = useEditorStore.getState().elements;

    for (const { viewIndex, component, material, texture } of assignments) {
      if (viewIndex !== activeViewIndex) continue;

      for (const id of component.elements) {
        if (!id.startsWith('grid')) {
          freedraw.actions.updateColorForElement(id, material.color);
        }

        if (id === component.id) {
          const element = elements.get(id) as BooleanElement | undefined;
          if (element?.type === 'boolean') {
            updateElement(elements, element, { fillColor: material.color });
          }
        }

        if (id === `grid-${component.id}`) {
          const element = elements.get(id) as GridElement | undefined;
          if (element) {
            await grid.actions.setMaterial(
              element,
              texture
                ? {
                    imageUrl: texture.src,
                    textureRealX: texture.textureRealX,
                    textureRealY: texture.textureRealY,
                  }
                : null,
            );
          }
        }
      }
    }

    await get().persistActiveView();
    general.actions.captureHistorySnapshot();
  };

  return {
    ...initialState,
    resetStore: () => {
      const currentTimeout = get().autosaveTimerRef?.current;
      if (currentTimeout) clearTimeout(currentTimeout);
      cachedActiveComponentId = '';
      set({ ...initialState, autosaveTimerRef: { current: null } });
    },
    addNewComponent: () => {
      const newComponent = createNewComponent();

      set((state) => ({
        components: [...state.components, newComponent],
        openedAccordionId: newComponent.id,
      }));

      boolean.actions.createBooleanGroup({
        id: newComponent.id,
        layer: getNextLayerForComponent(newComponent.id),
      });

      general.actions.captureHistorySnapshot();
    },
    deleteComponent: (id) => {
      get().deleteComponents([id]);
    },
    deleteComponents: (ids) => {
      if (useEditorRuntimeStore.getState().readonly) return;

      const state = get();
      if (ids.length === 0) return;

      const idsToDelete = new Set(ids);

      const elementsToDelete: string[] = [];
      for (const id of idsToDelete) {
        const component = state.components.find((c) => c.id === id);
        if (component) {
          elementsToDelete.push(...component.elements);
        }
      }

      useEditorStore.setState({ selectedElementIds: [] });

      if (elementsToDelete.length > 0) {
        general.actions.deleteElements(elementsToDelete);
      }

      const newComponents = state.components.filter(
        (c) => !idsToDelete.has(c.id),
      );

      if (newComponents.length === 0) {
        const newComponent = createNewComponent(1);

        set({
          components: [newComponent],
          openedAccordionId: newComponent.id,
        });

        boolean.actions.createBooleanGroup({
          id: newComponent.id,
          layer: getNextLayerForComponent(newComponent.id),
        });

        general.actions.captureHistorySnapshot();
        return;
      }

      const firstDeletedIndex = state.components.findIndex((c) =>
        idsToDelete.has(c.id),
      );
      const newIndex = Math.min(firstDeletedIndex, newComponents.length - 1);
      const newActiveComponentId = newComponents[newIndex].id;

      useEditorStore.setState({
        selectedElementIds: [newActiveComponentId],
      });

      set({
        components: newComponents,
        openedAccordionId: idsToDelete.has(state.openedAccordionId ?? '')
          ? newActiveComponentId
          : state.openedAccordionId,
      });
      general.actions.captureHistorySnapshot();
    },
    mergeComponents: (
      componentIds = deriveSelectedComponentIds(
        get().components,
        useEditorStore.getState().selectedElementIds,
      ),
    ) => {
      if (useEditorRuntimeStore.getState().readonly) return;

      const state = get();
      if (componentIds.length < 2) return;

      general.actions.pauseHistory();

      const activeComponentId = deriveActiveComponentId(
        state.components,
        useEditorStore.getState().selectedElementIds,
      );
      const activeComponent = state.components.find(
        (c) => c.id === activeComponentId,
      );

      const allElements = useEditorStore.getState().elements;
      const elementsToMove: GenericElement[] = [];

      for (const componentId of componentIds) {
        const component = state.components.find((c) => c.id === componentId);
        if (!component) continue;
        for (const elementId of component.elements) {
          const element = allElements.get(elementId);
          if (!isUserGeneratedElement(element)) continue;
          elementsToMove.push(element);
        }
      }

      const newComponent = createNewComponent();
      newComponent.color = activeComponent?.color ?? '#3c87f7';
      newComponent.materialId = activeComponent?.materialId;
      newComponent.shouldDisplayTexture =
        activeComponent?.shouldDisplayTexture ?? false;

      set({ components: [...state.components, newComponent] });

      boolean.actions.createBooleanGroup({
        id: newComponent.id,
        layer: getNextLayerForComponent(newComponent.id),
        fillColor: newComponent.color,
      });

      if (elementsToMove.length > 0) {
        updateMultipleElements(
          allElements,
          elementsToMove,
          elementsToMove.map(() => ({
            renderedById: newComponent.id,
            strokeColor: newComponent.color,
          })),
        );
      }

      set((s) => ({
        components: s.components.map((c) =>
          c.id === newComponent.id
            ? {
                ...c,
                elements: [...c.elements, ...elementsToMove.map((e) => e.id)],
              }
            : c,
        ),
      }));

      const movedElementIds = new Set(elementsToMove.map((e) => e.id));
      const elementIdsToDelete: string[] = [];
      for (const componentId of componentIds) {
        const component = state.components.find((c) => c.id === componentId);
        if (!component) continue;
        for (const elementId of component.elements) {
          if (!movedElementIds.has(elementId)) {
            elementIdsToDelete.push(elementId);
          }
        }
      }

      if (elementIdsToDelete.length > 0) {
        deleteMultipleElements(allElements, elementIdsToDelete);
      }

      set((s) => ({
        components: s.components.filter((c) => !componentIds.includes(c.id)),
      }));

      reassignAllComponentLayers();
      boolean.actions.reevaluateDimensions(newComponent.id);

      useEditorStore.setState({
        selectedElementIds: [newComponent.id],
        polygonPointsVisible: false,
      });
      set({ openedAccordionId: newComponent.id });

      renderStaticScene();
      renderInteractiveScene();

      general.actions.resumeHistory();
      general.actions.captureHistorySnapshot();
    },
    associateMaterialWithComponent: async (material) => {
      const state = get();
      const activeComponentId = getActiveComponentId();

      const activeComponent = state.components.find(
        (component) => component.id === activeComponentId,
      );

      if (!activeComponent) return;

      const materialUrl = material.seamlessTextureUrl();

      let color = material.hex;
      let shouldDisplayTexture = false;

      if (materialUrl) {
        color = (await new FastAverageColor().getColorAsync(materialUrl)).hex;
        shouldDisplayTexture = true;
      }

      const componentMaterial: ComponentMaterial = {
        materialId: material.id,
        color,
        shouldDisplayTexture,
      };
      let texture: ComponentTexture = null;

      if (materialUrl) {
        texture = {
          src: materialUrl,
          textureRealX: material.textureWidth || minimalDimensionSize,
          textureRealY: material.textureHeight || minimalDimensionSize,
        };
      }

      const views = await get().persistActiveView();

      // The material is applied to the active component and — if it is
      // part of a group — to all grouped components across the views.
      const groupId = activeComponent.groupId;
      const assignments = views.flatMap((view, viewIndex) =>
        (groupId
          ? view.components.filter((component) => component.groupId === groupId)
          : viewIndex === state.activeViewIndex
            ? [activeComponent]
            : []
        ).map((component) => ({
          viewIndex,
          component,
          material: componentMaterial,
          texture,
        })),
      );

      await applyMaterialAssignments(views, assignments);

      useCollectionStore.setState({
        activeType: material.type[0],
        appliedFilterOptions: [
          { option: 'producer', value: material.producer },
          {
            option: 'collection',
            value: material.collection,
          },
        ],
      });

      trackMaterialRendering('Editor', material);
    },
    replaceViewGroupMappings: async (mappings) => {
      if (useEditorRuntimeStore.getState().readonly || get().views.length < 2)
        return;

      const views = await get().persistActiveView();

      // All groups are rebuilt from scratch so stale mappings disappear.
      const nextViews = views.map((view) => ({
        ...view,
        components: view.components.map(
          (component): EditorComponent => ({
            ...component,
            groupId: undefined,
          }),
        ),
      }));

      const assignments: MaterialAssignment[] = [];

      for (const basis of nextViews[0].components) {
        const targets = mappings[basis.id];
        if (!targets || !Object.values(targets).some(Boolean)) continue;

        basis.groupId = basis.id;

        // Grouped components adopt the material of the basis component.
        const componentMaterial: ComponentMaterial = {
          materialId: basis.materialId,
          color: basis.color,
          shouldDisplayTexture: basis.materialId
            ? basis.shouldDisplayTexture
            : false,
        };
        const grid = views[0].elements.find(
          (element) => element.id === `grid-${basis.id}`,
        ) as GridElement | undefined;
        const texture: ComponentTexture =
          basis.materialId &&
          basis.shouldDisplayTexture &&
          grid?.fillMaterial?.src
            ? {
                src: grid.fillMaterial.src,
                textureRealX: grid.fillMaterial.textureRealX,
                textureRealY: grid.fillMaterial.textureRealY,
              }
            : null;

        for (const [viewIndex, view] of nextViews.entries()) {
          if (viewIndex === 0) continue;

          const target = view.components.find(
            (component) => component.id === targets[viewIndex],
          );
          if (!target) continue;

          target.groupId = basis.id;
          assignments.push({
            viewIndex,
            component: target,
            material: componentMaterial,
            texture,
          });
        }
      }

      await applyMaterialAssignments(nextViews, assignments);
    },
    associateElementWithComponent: (
      elementId,
      componentId = getActiveComponentId(),
    ) => {
      const state = get();

      let componentIndex = state.components.findIndex(
        (component) => component.id === componentId,
      );

      if (componentIndex === -1) {
        if (state.components.length === 0) return null;

        componentIndex = state.components.length - 1;
      }

      const newComponents = [...state.components];
      newComponents[componentIndex] = {
        ...newComponents[componentIndex],
        elements: [...newComponents[componentIndex].elements, elementId],
      };

      set({
        components: newComponents,
      });

      return newComponents[componentIndex];
    },
    disassociateElementWithComponent: (elementId) => {
      set((state) => {
        const componentIndex = state.components.findIndex((component) =>
          component.elements.includes(elementId),
        );

        if (componentIndex === -1) return state;

        const newComponents = [...state.components];
        newComponents[componentIndex] = {
          ...newComponents[componentIndex],
          elements: newComponents[componentIndex].elements.filter(
            (id) => id !== elementId,
          ),
        };

        return {
          components: newComponents,
        };
      });
    },
    moveComponent: (componentId, newIndex) => {
      set((state) => {
        const componentIndex = state.components.findIndex(
          (component) => component.id === componentId,
        );

        if (
          componentIndex === -1 ||
          newIndex < 0 ||
          newIndex >= state.components.length
        ) {
          return state;
        }

        const newComponents = [...state.components];
        const [movedComponent] = newComponents.splice(componentIndex, 1);
        newComponents.splice(newIndex, 0, movedComponent);

        return {
          components: newComponents,
          openedAccordionId: movedComponent.id,
        };
      });
      general.actions.captureHistorySnapshot();
    },
    moveElement: () => {},
    addView: async ({ src, htmlImage }) => {
      const state = get();

      if (
        state.isSwitchingViews ||
        !state.project?.id ||
        useEditorRuntimeStore.getState().readonly
      ) {
        return;
      }

      set({ isSwitchingViews: true });
      try {
        clearBooleanCaches();
        await get().persistActiveView();

        const newView: EditorView = { elements: [], components: [] };
        set((s) => ({ views: [...s.views, newView] }));

        await applyViewToEditor(newView, { clearHistory: true });
        set((s) => ({ activeViewIndex: s.views.length - 1 }));

        const imageElement = image.actions.addCenterImage(htmlImage, { src });
        general.actions.zoomToFitElement(imageElement);
        updateRuntimeStore({ panConstraintElementId: imageElement.id });

        history.clear();
        general.actions.captureHistorySnapshot();
      } finally {
        set({ isSwitchingViews: false });
      }
    },
    switchView: async (index) => {
      const { activeViewIndex, views, isSwitchingViews } = get();

      if (index === activeViewIndex || isSwitchingViews || !views[index]) {
        return;
      }

      set({ isSwitchingViews: true });
      try {
        clearBooleanCaches();
        await get().persistActiveView();
        await applyViewToEditor(views[index], { clearHistory: true });
        set({ activeViewIndex: index });
      } finally {
        set({ isSwitchingViews: false });
      }
    },
    persistActiveView: async () => {
      const { elements } = await general.actions.exportEditor();

      const { activeViewIndex, components, views } = get();

      const updatedViews =
        views.length === 0
          ? [{ elements, components }]
          : views.map((view, index) =>
              index === activeViewIndex
                ? { ...view, elements, components }
                : view,
            );

      set({ views: updatedViews });
      return updatedViews;
    },
    loadViews: async (
      views,
      {
        clearHistory,
        activeViewIndex = 0,
      }: { clearHistory?: boolean; activeViewIndex?: number } = {},
    ) => {
      set({ views, activeViewIndex });

      const targetView = views.at(activeViewIndex);
      if (!targetView) return;

      await applyViewToEditor(targetView, { clearHistory });
    },
    toggleFullscreen: () => {
      set((state) => ({ fullscreen: !state.fullscreen }));
      trackEvent('Projects', 'Fullscreen', 'Editor');
    },
    loadProject: (project, { activeViewIndex = 0 } = {}) => {
      if (!project) {
        return set({
          project: null,
          views: [],
          activeViewIndex: 0,
        });
      }

      const { data, ...meta } = project;

      set({
        project: meta,
        views: data ? repairProjectData(migrateProjectData(data)).views : [],
        activeViewIndex,
      });
    },
  };
});

const updateStrokeColors = () => {
  const activeComponentId = getActiveComponentId();

  const component = store
    .getState()
    .components.find((component) => component.id === activeComponentId);

  if (!component) return;

  freedraw.actions.setStrokeColor(component.color);
  retouching.actions.setStencilColor(component.color);
};

store.subscribe(updateStrokeColors);
useEditorStore.subscribe(updateStrokeColors);

export const useCustomEditorStore = createSelectorHooks(store);
